/**
 * User Preferences
 * Manages user-specific UI settings and preferences using IndexedDB-backed storage
 */

import { getStorage } from './storage';

export type InfoDensity = 'compact' | 'comfortable' | 'spacious';

export type UserPreferences = {
  infoDensity: InfoDensity;
};

export const DEFAULT_PREFERENCES: UserPreferences = {
  infoDensity: "comfortable",
};

/**
 * Get user preferences from storage
 */
export function getUserPreferences(): UserPreferences {
  try {
    const storage = getStorage();
    const prefs = storage.getAllUserPreferences();

    // Build preferences object from individual keys
    const result: UserPreferences = { ...DEFAULT_PREFERENCES };

    if (prefs.infoDensity) {
      result.infoDensity = prefs.infoDensity as InfoDensity;
    }

    return result;
  } catch (error) {
    console.error('Error loading user preferences:', error);
    return DEFAULT_PREFERENCES;
  }
}

/**
 * Save user preferences to storage
 */
export async function saveUserPreferences(preferences: Partial<UserPreferences>): Promise<void> {
  try {
    const storage = getStorage();

    // Save each preference individually
    for (const [key, value] of Object.entries(preferences)) {
      await storage.setUserPreference(key, String(value));
    }
  } catch (error) {
    console.error('Error saving user preferences:', error);
  }
}

/**
 * Update a single preference value
 */
export async function updatePreference<K extends keyof UserPreferences>(
  key: K,
  value: UserPreferences[K]
): Promise<void> {
  await saveUserPreferences({ [key]: value });
}
